import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Blobatar, Button } from '@forecastify/ui';
import { avatarSeed } from '@forecastify/shared';
import { useT } from '../i18n';
import { useOperatorSession } from '../session';
import { AuthCard, AuthInput, CODE_INPUT, TextInput } from '../chrome/auth-card';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Operator sign-in: the product's three steps, one session-issuing request. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **The product's page and the product's steps** — address, then password with the address and
 * its avatar kept on screen, then the code (asked for on 8 Oct 2026). What it leaves out is the
 * product's, not the console's: no *stay signed in* (an operator session lives in
 * `sessionStorage` for eight hours and that is the decision), no passkey, no Google, no sign-up.
 *
 * ⚠️⚠️ **THREE STEPS ON SCREEN, ONE REQUEST ON THE WIRE, AND THE SECOND HALF IS THE SECURITY
 * PROPERTY.** The server takes address, password and code together on purpose —
 * `superadmin-auth.service.ts`: a multi-step protocol has to represent *"password accepted, factor
 * pending"* as something the client holds, and that something is a credential one debugging session
 * away from being accepted on its own.
 *
 * ⚠️ 2 Nov 2026 · **the password step asks the server before the code step appears**
 * (`sign-in/password`), because moving on unasked meant a mistyped password was found out only
 * after the code had been typed as well. That check answers and issues nothing, so the property
 * above holds: the session still comes from one request carrying all three.
 *
 * ⚠️ **A refusal is one sentence whatever was wrong** — the server answers an unknown address, a
 * wrong password and a wrong code identically. It shows on the step that was refused, and
 * *Módosítás* beside the address is one click back to a mistyped one.
 *
 * ⚠️ **No link to `/enrol` and none to anything that reads as registration** (asked for on 8 Oct
 * 2026). An invited operator signs up from the invitation's link (`decisions.md` S-21); `/enrol` is
 * left for the support path where a factor was cleared from the command line, and whoever ran that
 * command sends the address.
 */
type Step = 'email' | 'password' | 'code';

export function SignInScreen(): ReactNode {
  const t = useT();
  const session = useOperatorSession();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  /* ⚠️ Focus follows the step, because the person just pressed *Tovább* and their hands are on the
     keyboard. A ref and an effect rather than `autoFocus` on the later fields: they arrive by the
     previous step's own submit, never on page load. */
  const passwordField = useRef<HTMLInputElement>(null);
  const codeField = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (step === 'password') passwordField.current?.focus();
    if (step === 'code') codeField.current?.focus();
  }, [step]);

  const back = (): void => {
    setStep('email');
    setPassword('');
    setCode('');
    setFailure(null);
  };

  const submit = (): void => {
    if (busy) return;
    if (step === 'email') {
      if (email.trim() === '') return;
      setStep('password');
      return;
    }
    if (step === 'password') {
      if (password === '') return;
      /* ⚠️ 2 Nov 2026 · the password is CHECKED before the code step appears — it used to move on
       * unasked, and a wrong password surfaced only after the code was typed too. The check issues
       * nothing: the request below still carries all three (S-8). */
      setBusy(true);
      setFailure(null);
      void session.checkPassword({ email: email.trim(), password }).then((result) => {
        setBusy(false);
        if (result.ok) {
          setStep('code');
          return;
        }
        setFailure(result.code === 'unreachable' ? t('signIn.unreachable') : t('signIn.refused'));
      });
      return;
    }
    if (!/^\d{6}$/u.test(code)) {
      setFailure(t('signIn.codeShape'));
      return;
    }
    setBusy(true);
    setFailure(null);
    void session.signIn({ email: email.trim(), password, totpCode: code }).then((result) => {
      if (result.ok) return;
      setBusy(false);
      /* ⚠️ `unreachable` is the one refusal worth telling apart: "the server is not answering" is
       * actionable and "your details are wrong" is not, when the server is simply down. */
      setFailure(result.code === 'unreachable' ? t('signIn.unreachable') : t('signIn.refused'));
      /* The code is single-use — a spent one left in the field fails the retry for a second reason. */
      setCode('');
    });
  };

  return (
    <AuthCard screen="sign-in" title={t('signIn.submit')}>
      <form
        className="flex flex-col gap-3.5"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {step === 'email' ? (
          <AuthInput label={t('signIn.email')}>
            <TextInput
              type="email"
              autoComplete="username"
              placeholder={t('signIn.emailPlaceholder')}
              /* A single-purpose screen with one obvious first field — the product's own call. */
              autoFocus
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
          </AuthInput>
        ) : (
          /* ⚠️ The address stays on screen with a way back to it — the product's reason: a
             multi-step form that has hidden what you typed is one you cannot check, and a typo
             surfaces as a refusal that reads like a wrong password. */
          <div className="flex flex-col items-center gap-2.5 pb-1">
            <span className="bg-on-id-light flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-full">
              <span className="size-[84px] shrink-0">
                <Blobatar seed={avatarSeed(email)} size={84} animate="always" />
              </span>
            </span>
            <span className="text-muted-foreground max-w-full truncate text-center text-[12.5px]">
              {t('signIn.loggingInAs')}{' '}
              <span className="text-foreground font-medium">{email.trim()}</span>{' '}
              <button type="button" className="text-primary font-medium" onClick={back}>
                {t('signIn.changeEmail')}
              </button>
            </span>
          </div>
        )}

        {step === 'password' ? (
          <AuthInput label={t('signIn.password')}>
            <TextInput
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              ref={passwordField}
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
            />
          </AuthInput>
        ) : null}

        {step === 'code' ? (
          <AuthInput label={t('signIn.code')}>
            <TextInput
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="000000"
              className={CODE_INPUT}
              ref={codeField}
              value={code}
              onChange={(event) => {
                setCode(event.target.value.replace(/\D/gu, '').slice(0, 6));
                setFailure(null);
              }}
            />
          </AuthInput>
        ) : null}

        {failure === null ? null : (
          <p role="alert" className="text-destructive text-[12px]">
            {failure}
          </p>
        )}

        <Button
          type="submit"
          size="lg"
          className="w-full"
          disabled={
            busy ||
            (step === 'email' && email.trim() === '') ||
            (step === 'password' && password === '') ||
            (step === 'code' && code.length !== 6)
          }
        >
          {busy ? t('signIn.working') : step === 'code' ? t('signIn.submit') : t('signIn.continue')}
        </Button>
      </form>
    </AuthCard>
  );
}
